import * as Sentry from '@sentry/react'

let initialized = false

const texts = {
  de: {
    formTitle: 'Etwas aufgefallen?',
    messageLabel: 'Was möchtest du mitteilen?',
    messagePlaceholder: 'Ein Fehler, eine Idee, ein Wunsch – alles willkommen.',
    submitButtonLabel: 'Senden',
    cancelButtonLabel: 'Schließen',
    addScreenshotButtonLabel: 'Screenshot hinzufügen',
    removeScreenshotButtonLabel: 'Screenshot entfernen',
    successMessageText: 'Danke für dein Feedback!',
    isRequiredLabel: '(Pflichtfeld)',
    highlightToolText: 'Hervorheben',
    hideToolText: 'Verbergen',
    removeHighlightText: 'Entfernen',
  },
  en: {
    formTitle: 'Send Feedback',
    messageLabel: 'Description',
    messagePlaceholder: 'What happened? What did you expect?',
    submitButtonLabel: 'Submit',
    cancelButtonLabel: 'Cancel',
    addScreenshotButtonLabel: 'Add screenshot',
    removeScreenshotButtonLabel: 'Remove screenshot',
    successMessageText: 'Thanks for your feedback!',
    isRequiredLabel: '(required)',
  },
}

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) return

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.feedbackIntegration({
        autoInject: false,
      }),
    ],
  })
  initialized = true

  // Catch JS errors and unhandled promise rejections that escape React's error
  // boundary (e.g. async work in event handlers, fire-and-forget promises).
  // Sentry's React SDK does NOT auto-install these globals when no
  // BrowserApiErrors integration is configured — we add them explicitly.
  window.addEventListener('error', (e) => {
    Sentry.captureException(e.error ?? new Error(e.message), {
      tags: { source: 'window.error' },
    })
  })

  window.addEventListener('unhandledrejection', (e) => {
    Sentry.captureException(
      e.reason instanceof Error ? e.reason : new Error(String(e.reason)),
      { tags: { source: 'unhandledrejection' } },
    )
  })
}

// Block keyboard shortcuts while typing in the Sentry feedback form.
// Events from the Shadow DOM bubble to the main document — intercept them
// in the capture phase before any app handler sees them.
function installKeyboardGuard() {
  window.addEventListener('keydown', (e) => {
    const path = e.composedPath()
    if (path.some(el => el instanceof HTMLElement && el.id === 'sentry-feedback')) {
      e.stopPropagation()
    }
  }, true) // capture phase
}
installKeyboardGuard()

function getCurrentLanguage(): 'de' | 'en' {
  const lang = localStorage.getItem('language') ?? navigator.language
  return lang.startsWith('de') ? 'de' : 'en'
}

/** Opens the Sentry feedback form in the current app language. No-op if Sentry is not initialized. */
export function openFeedbackForm() {
  if (!initialized) return

  const lang = getCurrentLanguage()
  const localizedFeedback = Sentry.feedbackIntegration({
    colorScheme: 'system',
    enableScreenshot: true,
    showName: false,
    showEmail: true,
    autoInject: false,
    showBranding: false,
    useSentryUser: { email: 'email' },
    ...texts[lang],
  })

  localizedFeedback.createForm().then(form => {
    form.appendToDom()
    form.open()
  }).catch(err => {
    console.error('[Sentry] Failed to open feedback form:', err)
  })
}
